import { USPAPICall, USPAPIReturn, TCData, USPData } from '../interfaces';
import { USPrivacyString } from './USPrivacyString';
import { CCPA_LOCATIONS } from '../utils/constants';
import { cookie } from '@turnercode/cdp-cookie';

export const uspString = new USPrivacyString();

let tcfConsentString: string | null = null;

export function getTCFConsentString(): string | null {
  return tcfConsentString;
}

/**
 * The TCF state as reported by the CMP, captured verbatim rather than reduced to
 * PRISM's own load/no-load decision.
 *
 * Mosaic applies consent downstream (C360 ID Graph) instead of gating capture on
 * the client, so its events carry the raw purpose grants and let the backend
 * decide (DAP-114771, PRD 068). Purposes are keyed by TCF purpose id, and
 * consent is kept separate from legitimate interest so no policy is baked in
 * here.
 */
export type TCFSnapshot = {
  tcString: string | null;
  purposeConsents: Record<string, boolean>;
  purposeLegitimateInterests: Record<string, boolean>;
};

let tcfSnapshot: TCFSnapshot | null = null;

/** The last TCF state seen by `checkOutsideUS`, or `null` if no CMP responded. */
export function getTCFSnapshot(): TCFSnapshot | null {
  return tcfSnapshot;
}

/** Clear the cached TCF state (primarily for tests). */
export function resetTCFSnapshot(): void {
  tcfSnapshot = null;
  tcfConsentString = null;
  tcfListenerId = null;
  tcfSubscribed = false;
  stopWaitingForCmp();
}

/** Keep only the purposes the CMP actually granted, so absent ids stay absent
 * rather than becoming an explicit `false` we never received. */
function grantedPurposes(source: Record<string, boolean | undefined> | undefined): Record<string, boolean> {
  const granted: Record<string, boolean> = {};
  Object.entries(source || {}).forEach(([id, value]) => {
    if (value === true) {
      granted[id] = true;
    }
  });
  return granted;
}

function captureTCFSnapshot(tcData: TCData): void {
  tcfSnapshot = {
    tcString: tcData.tcString || null,
    purposeConsents: grantedPurposes(tcData.purpose?.consents as Record<string, boolean | undefined>),
    purposeLegitimateInterests: grantedPurposes(
      tcData.purpose?.legitimateInterests as Record<string, boolean | undefined>,
    ),
  };
}

let tcfListenerId: number | null = null;
/** True once a listener has been handed to the CMP (or to its stub). */
let tcfSubscribed = false;
let tcfWatchTimer: ReturnType<typeof setInterval> | null = null;

/** CMP states where the TC data is settled and safe to record. `cmpuishown` is
 * skipped because the user has not acted yet. */
const SETTLED_TCF_EVENT_STATUSES = ['tcloaded', 'useractioncomplete'];

/** How long to keep waiting for a CMP that loads after PRISM, and how often to
 * look. Editorial pages routinely load the CMP asynchronously, well after the
 * SDK has initialized. */
const TCF_WAIT_POLL_MS = 250;
const TCF_WAIT_TIMEOUT_MS = 15000;

function stopWaitingForCmp(): void {
  if (tcfWatchTimer !== null) {
    clearInterval(tcfWatchTimer);
    tcfWatchTimer = null;
  }
}

/** Hand a listener to the CMP. Returns false when there is no CMP to talk to. */
function subscribeToCmp(): boolean {
  if (typeof window?.__tcfapi !== 'function') {
    return false;
  }
  try {
    window.__tcfapi('addEventListener', 2, (tcData: TCData, success: boolean) => {
      if (!success || !tcData) {
        return;
      }
      if (typeof tcData.listenerId === 'number') {
        tcfListenerId = tcData.listenerId;
      }
      if (SETTLED_TCF_EVENT_STATUSES.includes(tcData.eventStatus)) {
        captureTCFSnapshot(tcData);
        if (tcData.tcString) {
          tcfConsentString = tcData.tcString;
        }
      }
    });
  } catch (error) {
    console.log('[PSM]: Error subscribing to TCF consent changes', error);
  }
  // The CMP exists, so the listener is registered (a stub queues it until the
  // real CMP loads). Either way there is nothing left to wait for.
  tcfSubscribed = true;
  return true;
}

/**
 * Keep the TCF snapshot current for the life of the page by subscribing to the
 * CMP.
 *
 * PRISM resolves consent once during init, which in EMEA is typically before the
 * user has answered the consent banner. Mosaic events keep firing after that
 * (SPA pageviews, link and slot events), and since the backend honors the
 * consent it receives, a snapshot frozen at "not answered yet" would have
 * downstream discard events the user went on to consent to.
 *
 * Idempotent; only registers with the CMP once per page.
 */
export function watchTCFConsent(): void {
  if (tcfSubscribed || tcfWatchTimer !== null) {
    return;
  }
  if (subscribeToCmp()) {
    return;
  }
  // No CMP on the page yet. Keep looking for a bounded window instead of giving
  // up: PRISM often initializes before the CMP script, and a one-shot check here
  // would report "no consent" for the rest of the page even after the user
  // accepts the banner.
  const deadline = Date.now() + TCF_WAIT_TIMEOUT_MS;
  tcfWatchTimer = setInterval(() => {
    if (subscribeToCmp() || Date.now() >= deadline) {
      stopWaitingForCmp();
    }
  }, TCF_WAIT_POLL_MS);
}

/** Unsubscribe from CMP consent changes registered by `watchTCFConsent`. */
export function unwatchTCFConsent(): void {
  stopWaitingForCmp();
  tcfSubscribed = false;
  if (tcfListenerId === null) {
    return;
  }
  try {
    if (typeof window?.__tcfapi === 'function') {
      window.__tcfapi('removeEventListener', 2, () => undefined, tcfListenerId);
    }
  } catch (error) {
    console.log('[PSM]: Error unsubscribing from TCF consent changes', error);
  }
  tcfListenerId = null;
}

const LIVERAMP_VENDOR_ID = 97;

export function hasLiveRampTCFConsent(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window?.__tcfapi !== 'function') {
      resolve(false);
      return;
    }
    window.__tcfapi('getTCData', 2, (tcData: TCData, success: boolean) => {
      if (!success || !tcData) {
        resolve(false);
        return;
      }
      const vendorConsent = tcData.vendor?.consents?.[LIVERAMP_VENDOR_ID] === true;
      const purposeOneConsent = tcData.purpose?.consents?.[1] === true;
      resolve(vendorConsent && purposeOneConsent);
    });
  });
}

/**
 * Internal function to find a frame, if it exists
 *
 * @param {string} locator - Locator string to use to find frame
 * @returns {object} - Child frame object if found, else null
 */
export function findFrame(locator: string): HTMLElement | HTMLIFrameElement {
  let cmpFrame = null;
  let w: Window;

  for (w = window; w; w = w.parent) {
    try {
      if (w.frames && w.frames[locator]) {
        cmpFrame = w;
        break;
      }
    } catch (e) {
      throw new Error(e);
    }
    if (w === window.top) {
      break;
    }
  }
  return cmpFrame;
}

function createIabStub(locatorName: string): void {
  const addFrame = (): boolean => {
    if (!window.frames[locatorName]) {
      if (document.body) {
        const iframe = document.createElement('iframe');

        iframe.style.cssText = 'display:none';
        iframe.name = locatorName;
        document.body.appendChild(iframe);
      } else {
        setTimeout(addFrame, 5);
      }
      return true;
    }
    return false;
  };

  const uspMsgHandler = (event: MessageEvent) => {
    try {
      const msgIsString = typeof event.data === 'string';
      let i: USPAPICall['__uspapiCall'];
      let json: USPAPICall;

      try {
        json = msgIsString ? JSON.parse(event.data) : event.data;
      } catch (e) {
        json = {};
      }
      if (typeof json === 'object' && json !== null && '__uspapiCall' in json) {
        i = json.__uspapiCall;
        window.__uspapi(
          i.command,
          i.version,
          function (retValue: string, success: boolean) {
            const returnMsg: USPAPIReturn = {};

            returnMsg.__uspapiReturn = {
              returnValue: retValue,
              success,
              callId: i.callId,
            };

            if (event.source instanceof Window) {
              event.source.postMessage(msgIsString ? JSON.stringify(returnMsg) : returnMsg, '*');
            }
          },
          i.parameter,
        );
      }
    } catch (e) {
      // TODO - log error ... errorLogger('Privacy', '_uspMsgHandler', e);
    }
  };

  const usingUspFrame = findFrame('__uspapiLocator');

  /* CCPA IAB handler must always be defined, even in GDPR regions... */
  if (usingUspFrame === null) {
    /* But only for the top frame */
    addFrame();
    window.__uspapi['msgHandler'] = uspMsgHandler;
    window.addEventListener('message', window.__uspapi['msgHandler'], false);
  }
}

export function initPrivacy(enabled = true, countryCode: string) {
  const api = {
    isPrivacyEnabled(): boolean {
      return enabled;
    },
    getUSPString(): string {
      return uspString.getUSPrivacyString();
    },
    getUSPData(): USPData {
      return {
        version: uspString.getVersion(),
        uspString: uspString.getUSPrivacyString(),
      };
    },
    setUSPString(str: string): boolean {
      return uspString.setUSPrivacyString(str);
    },
    ccpaShareData(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YNN');
      }
      return uspString.getUSPrivacyString();
    },
    ccpaDoNotShare(): string {
      if (CCPA_LOCATIONS.includes(countryCode)) {
        uspString.setUSPrivacyString('1YYN');
      }
      return uspString.getUSPrivacyString();
    },
  };

  if (enabled) {
    window.__uspapi = function __uspapi(
      command: string,
      version: number,
      callback: (uspData: USPData, success: boolean) => void,
    ): void {
      if (command === 'getUSPData' && Number(version) === 1) {
        callback({ version, uspString: uspString.getUSPrivacyString() }, true);
      } else if (command === 'ccpaDoNotShare') {
        callback({ version, uspString: api.ccpaDoNotShare() }, true);
      } else if (command === 'ccpaShareData') {
        callback({ version, uspString: api.ccpaShareData() }, true);
      } else if (command === 'ping') {
        callback({ version, uspapiLoaded: true }, true);
      } else {
        callback(null, false);
      }
    };
    createIabStub('__uspapiLocator');

    const str = cookie.get('usprivacy') as string;

    if (str) {
      uspString.setUSPrivacyString(str);
    } else if (CCPA_LOCATIONS.includes(countryCode)) {
      uspString.setUSPrivacyString('1YNN');
    } else {
      uspString.setUSPrivacyString('1---');
    }

    window.WM = window.WM || {};
    window.WM.PSM = window.WM.PSM || {};
    Object.assign(window.WM?.PSM, api);
  }

  return api;
}

export function checkOutsideUS(brand: string): Promise<{ shouldLoad: boolean; categories: Record<string, boolean>[] }> {
  return new Promise((resolve) => {
    try {
      // IAB config consent categories
      const defaultCategories = {
        '1': 'data-store',
        '2': 'ads-contextual',
        '3': 'ads-person-prof',
        '4': 'ads-person',
        '5': 'content-person-prof',
        '6': 'consent-person',
        '7': 'measure-ads',
        '8': 'measure-content',
        '9': 'measure-market',
        '10': 'product-develop',
      };

      // Adding in brand specific logic for consent category
      const brandCategories = { ...defaultCategories };
      if (brand === 'Bleacher Report') {
        delete brandCategories[5];
        delete brandCategories[6];
        delete brandCategories[8];
      }

      // Checking the consent by prioritizing TCF > UCL > OneTrust
      // Check for TCF API (GDPR/EMEA only)
      if (typeof window?.__tcfapi === 'function') {
        window.__tcfapi('getTCData', 2, (tcData: TCData, success: boolean) => {
          // Snapshot whatever the CMP returned, including a partial or all-denied
          // response, so Mosaic can report the real state instead of inferring it
          // from PRISM's refusal to load.
          if (success && tcData) {
            captureTCFSnapshot(tcData);
          }
          if (success && 'purpose' in tcData && 'legitimateInterests' in tcData.purpose) {
            if (tcData.tcString) {
              tcfConsentString = tcData.tcString;
            }
            const combined = {
              ...(tcData.purpose.legitimateInterests || {}),
              ...(tcData.purpose.consents || {}),
            };
            const result: Record<string, boolean>[] = [];
            let shouldLoad = true;

            Object.entries(brandCategories).forEach(([id, name]) => {
              if (combined[id]) {
                result.push({ [name]: combined[id] });
              } else {
                result.push({ [name]: false });
                shouldLoad = false;
              }
            });

            resolve({
              shouldLoad,
              categories: result.concat([
                { 'special-purpose-1': true },
                { 'special-purpose-2': true },
                { 'feature-1': true },
                { 'feature-2': true },
                { 'feature-3': true },
              ]),
            });
          } else {
            resolve({
              shouldLoad: false,
              categories: [],
            });
          }
        });
        return;
      }

      // Check for the User Consent library (UCL)
      if (typeof window?.WBD?.UserConsent?.inUserConsentState === 'function') {
        const supersetConfigCategories = {
          ...brandCategories,
          '11': 'targeting',
          '12': '3p-targeting',
          '13': 'performance',
          '14': 'functional',
        };
        const defaultCategoriesArray = Object.values(supersetConfigCategories);
        const defaultCategoriesConsent = Object.values(supersetConfigCategories).map((category) => ({
          [category]: true,
        }));

        // Checking user consent for all possible consent categories
        const userConsent: boolean = window.WBD.UserConsent.inUserConsentState(defaultCategoriesArray) === true;
        resolve({
          shouldLoad: userConsent,
          categories: userConsent ? defaultCategoriesConsent : [],
        });
        return;
      }

      // Check for OneTrust
      if (typeof window?.OnetrustActiveGroups === 'string') {
        const oneTrustRequiredValuesWM = ['tc', 'fc', 'pf'];
        const oneTrustRequiredValuesWB = ['tc', 'fc', 'pc'];
        const oneTrustRequiredValuesWBD = ['tc', 'ftc', 'pfc'];
        const oneTrustRequiredValuesDiscovery = ['C0002', 'C0003', 'C0004'];
        const oneTrustRequiredCategories = [{ Targeting: true }, { Functional: true }, { Performance: true }];
        const oneTrustFoundValues = window.OnetrustActiveGroups.trim()
          ? window.OnetrustActiveGroups.trim().split(',')
          : [];

        const userConsent =
          oneTrustRequiredValuesWM.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesWB.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesWBD.every((value) => oneTrustFoundValues.includes(value)) ||
          oneTrustRequiredValuesDiscovery.every((value) => oneTrustFoundValues.includes(value));

        resolve({
          shouldLoad: userConsent,
          categories: userConsent ? oneTrustRequiredCategories : [],
        });
        return;
      }
      resolve({
        shouldLoad: false,
        categories: [],
      });
    } catch (error) {
      console.log('[PSM]: Error encountered during consent check', error);
      resolve({
        shouldLoad: false,
        categories: [],
      });
    }
  });
}
